import React from 'react';

interface ProductDisclaimersProps {
  description: string;
  showLearnMoreLinks?: boolean;
}

const DAMASCUS_TYPES = [
  'Woodgrain', 'Flattwist', 'Marble', 'Tightweave', 'Kinetic', 'Damascus', 'Sunset'
];

const CAMO_TYPES = [
  "King's Field", "King's Mountain", "King's Snow", "King's Woodland", "King's Pink", "King's Desert",
  'RealTree Advantage Max', 'RealTree AP', 'RealTree APC Black', 'RealTree APC Green',
  'RealTree APC Maroon', 'RealTree APC Orange', 'RealTree APC Pink', 'RealTree APC Purple',
  'RealTree APC Red', 'RealTree APC Snow', 'RealTree Timber', 'RealTree Advantage Max-4',
  'RealTree APC Navy Blue', 'RealTree APG',
  'MossyOak Bottomland', 'MossyOak Break Up Infinity', 'MossyOak Duck Blind',
  'MossyOak Obsession', 'MossyOak SG Blades', 'MossyOak Treestand'
];

const WOOD_TYPES = [
  'Blood Wood', 'Bocote', 'Boxelder Burl', 'Cocobolo', 'Desert Iron Wood',
  'Ebony', 'Koa', 'Leopard Wood', 'Madagascar Rose Wood', 'Maple Burl',
  'Natural Oak', 'Osage Orange', 'Padauk', 'Pernambuco', 'Pyinmaburl',
  'Red Heart', 'Sapele', 'Sequoia', 'Snake', 'Spalted Tamarind', 'Teak',
  'Thuya Burl', 'Walnut', 'Wenge', 'Whiskey Barrel', 'Ziricote'
];

const ProductDisclaimers = ({ description, showLearnMoreLinks = false }: ProductDisclaimersProps) => {
  const disclaimers: React.ReactNode[] = [];

  // Remove "Titanium Damascus" before checking for Damascus types to avoid false positives
  const descriptionWithoutTitaniumDamascus = description?.replace(/Titanium Damascus/g, '') || '';
  const hasDamascus = DAMASCUS_TYPES.some(type => descriptionWithoutTitaniumDamascus.includes(type));
  if (hasDamascus) {
    disclaimers.push(
      <li key="damascus-1" className="disclaimer">
        Our Acid or Tumble Finished Damascus Steel is grey, not black in color.
        {showLearnMoreLinks && (
          <>
            {' '}
            <a href="/pages/damascus-finish-info" rel="nofollow" target="_blank" className="tw-underline" style={{ color: 'var(--brand-color)' }}>
              Learn more about our Damascus Finishes
            </a>.
          </>
        )}
      </li>,
      <li key="damascus-2" className="disclaimer">
        The acid used in the acid wash Damascus can only penetrate a small way into the Damascus so it will fade over time to a dull polish finish. The finish is covered by our <strong>Lifetime Warranty</strong> and can be restored to a like new finish.
      </li>,
      <li key="damascus-3" className="disclaimer">
        Damascus Steel is a hand-forged and folded metal, so the pattern lines will vary from ring to ring.
      </li>
    );
  }

  if (description?.includes('Zirconium')) {
    disclaimers.push(
      <li key="zirconium" className="disclaimer">
        Black Zirconium is the darkest metal available; however, it is not pure black and in some lighting it can appear to be dark grey.
        {showLearnMoreLinks && (
          <>
            {' '}
            <a href="/pages/black-zirconium-ring-info" rel="nofollow" target="_blank" className="tw-underline" style={{ color: 'var(--brand-color)' }}>
              Learn more about Black Zirconium
            </a>.
          </>
        )}
      </li>
    );
  }

  if (description?.includes('Titanium Damascus')) {
    disclaimers.push(
      <li key="titanium-damascus" className="disclaimer">
        Titanium Damascus rings will have a color variation of blue, purple, red, brown and/or silver. We cannot guarantee the pattern or colors on specific cuts of Titanium Damascus as the heating and anodizing process that brings that out is done after the ring is made. Unfortunately, we have no way of predicting which colors or pattern will be on your ring as the anodizing takes a little differently on every cut of Titanium Damascus despite being exposed to the exact same process.
      </li>
    );
  }

  if (description?.includes('Tantalum Noir')) {
    disclaimers.push(
      <li key="tantalum-noir" className="disclaimer">
        Darkened Tantalum is created through a proprietary heating and cooling process which creates a dark layer on the surface of the Tantalum. Darkened Tantalum is pure Tantalum and does not have any added chemicals or coatings. However, this darkened layer can scratch to reveal the natural color of Tantalum below and should be treated with care like any fine jewelry.
        {showLearnMoreLinks && (
          <>
            {' '}
            <a href="/pages/tantalum-ring-info" rel="nofollow" target="_blank" className="tw-underline" style={{ color: 'var(--brand-color)' }}>
              Learn more about our Tantalum Rings
            </a>.
          </>
        )}
      </li>
    );
  }

  const hasCamo = CAMO_TYPES.some(type => description?.includes(type));
  if (hasCamo) {
    disclaimers.push(
      <li key="camo" className="disclaimer">
        Camo inlays and sleeves have a small seam where the two ends of the material meet.
      </li>
    );
  }

  if (description?.includes('Antler')) {
    disclaimers.push(
      <li key="antler" className="disclaimer">
        We use <strong>100% natural Antler</strong> from shed Antler finds. As the Antler is natural, we cannot guarantee the exact coloration of the antler that you will receive in your custom ring. Antler colors vary from white or gray to tan or brown and can range in brightness and pattern as well.
      </li>
    );
  }

  const hasWood = WOOD_TYPES.some(wood => description?.includes(wood));
  if (hasWood) {
    disclaimers.push(
      <li key="wood" className="disclaimer">
        As wood is a natural material it can vary in pattern, shade, and even color from piece to piece depending on our current supply. This is part of the appeal to wood as it does mean every ring is unique. However, because of this we cannot guarantee a specific shade, color, or pattern within a specific wood type.
      </li>
    );
  }

  if (description?.includes('Abalone') || description?.includes('Mother of Pearl')) {
    disclaimers.push(
      <li key="shell" className="disclaimer">
        As the shell is natural, we cannot guarantee the exact coloration of the shell that you will receive in your custom ring.
        {showLearnMoreLinks && (
          <>
            {' '}
            <a href="/pages/shell-ring-info" rel="nofollow" target="_blank" className="tw-underline" style={{ color: 'var(--brand-color)' }}>
              Learn more about our Shell Inlays.
            </a>
          </>
        )}
      </li>
    );
  }

  if (description?.includes('Superconductor')) {
    disclaimers.push(
      <li key="superconductor-1" className="disclaimer">
        Superconductor is an industrial material, it's not made specifically for jewelry, so it can have some flaws in it that come out during the etching process. These flaws can vary from small voids in the material to black markings. The industrial nature of superconductor is part of what gives it the rugged appeal that many people enjoy, but it is something to consider when ordering.
      </li>,
      <li key="superconductor-2" className="disclaimer">
        Sometimes, with a new ring, the copper within the Superconductor can cause some skin discoloration, but over time that will go away and not be a continuing issue.
      </li>
    );
  }

  if (disclaimers.length === 0) {
    return null;
  }

  return (
    <div className="product-disclaimers tw-text-sm tw-text-gray-700">
      <strong>Please Note:</strong>
      <ul className="tw-mt-2 tw-mb-0 tw-pl-5 tw-list-disc tw-space-y-2">
        {disclaimers}
      </ul>
    </div>
  );
};

export default ProductDisclaimers;
